import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { motion, AnimatePresence, useScroll, useTransform, useInView } from 'framer-motion';
import {
    Check as CheckIcon,
    ArrowForward as ArrowForwardIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    FormatQuote as QuoteIcon,
    AccessTime as ClockIcon,
    TrendingUp as TrendingUpIcon,
    People as UsersIcon,
} from '@mui/icons-material';

// Styles
const styles = {
    section: {
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 6, lg: 8 },
        width: '100%',
    },
    container: {
        maxWidth: '1280px',
        px: { xs: 2, sm: 3, lg: 4 },
    },
    gradientBg: {
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(to bottom, rgba(239, 246, 255, 0.3), rgba(255, 255, 255, 0.7))',
    },
    accentColor: '#00C2FF',
    primaryBtn: {
        backgroundColor: '#000',
        color: '#fff',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
        },
        px: 4,
        py: 1.5,
        fontSize: '1.125rem',
        textTransform: 'none',
    },
};

// ============ HERO SECTION ============
function ForExpertsHero() {
    return (
        <Box
            component="section"
            sx={{
                position: 'relative',
                backgroundColor: 'white',
                pt: 10,
                pb: 16,
                overflow: 'hidden',
            }}
        >
            <Container sx={styles.container}>
                <Grid container spacing={6} alignItems="center">
                    <Grid item xs={12} lg={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: '1.875rem', sm: '3rem', xl: '3.75rem' },
                                    fontWeight: 700,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                Your Expertise, Your AI Persona
                            </Typography>
                            <Typography
                                sx={{
                                    maxWidth: '600px',
                                    color: 'text.secondary',
                                    fontSize: { xs: '1rem', md: '1.25rem' },
                                }}
                            >
                                Share your knowledge with the world 24/7. Transform your expertise into an AI persona that can help thousands.
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    sx={styles.primaryBtn}
                                    href="/?createAccount=true"
                                >
                                    Create Your AI Persona
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CheckIcon sx={{ color: styles.accentColor, fontSize: 16 }} />
                                    <Typography variant="body2">No coding required</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CheckIcon sx={{ color: styles.accentColor, fontSize: 16 }} />
                                    <Typography variant="body2">Earn passive income</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: { xs: '100%', lg: 500, xl: 600 },
                                    height: { xs: 300, lg: 500, xl: 600 },
                                }}
                            >
                                <Box
                                    component="img"
                                    src="https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/creator-section/experts-hero.png"
                                    alt="AI Expert Illustration"
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
}

// ============ CREATORS SECTION ============
const features = [
    {
        title: "Human-Like Interactions",
        description: "Transform your knowledge, creativity, and brand into scalable, interactive experiences that engage and earn—no coding required.",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/creator-section/C1.png"
    },
    {
        title: "Monetization Built-In",
        description: "Offer premium sessions, courses, and Q&A opportunities without handling complex payment structures.",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/creator-section/C2.png"
    },
    {
        title: "Real-Time Training & Verification",
        description: "Continuously refine your AI persona's knowledge. A verified persona boosts credibility, trust, and authority.",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/creator-section/C3.png"
    },
    {
        title: "Multi-Channel Integration",
        description: "Train personas from your existing YouTube content, articles, or podcasts, ensuring a consistent brand voice across platforms.",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/creator-section/C4.png"
    }
];

function CreatorsSection() {
    return (
        <Box component="section" sx={{ ...styles.section, py: { xs: 8, lg: 12 } }}>
            <Box sx={styles.gradientBg} />
            <Container sx={{ ...styles.container, position: 'relative' }}>
                <Box sx={{ maxWidth: '900px', mx: 'auto', textAlign: 'center' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <Typography sx={{ color: '#0077CC', fontSize: '1.25rem', mb: 2 }}>
                            FOR EXPERTS
                        </Typography>
                        <Typography
                            variant="h3"
                            sx={{
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                fontWeight: 700,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            Grow Your Influence, Monetize Your Expertise
                        </Typography>
                        <Typography
                            sx={{
                                mt: 2,
                                mx: 'auto',
                                maxWidth: '42rem',
                                color: 'text.secondary',
                                fontSize: '1.125rem',
                            }}
                        >
                            Turn Your Expertise into a 24/7 AI Persona, Monetize Your Knowledge, and
                            Effortlessly Engage New Audiences Everywhere.
                        </Typography>
                    </motion.div>
                </Box>

                <Grid container spacing={3} sx={{ mt: 6 }}>
                    {features.map((feature, index) => (
                        <Grid item xs={12} sm={6} lg={3} key={feature.title}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.2 }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        '&:hover img': {
                                            transform: 'scale(1.05)',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            mb: 2,
                                            overflow: 'hidden',
                                            borderRadius: 4,
                                            backgroundColor: 'rgba(243, 244, 246, 0.8)',
                                            width: '100%',
                                            aspectRatio: '3/4',
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={feature.image}
                                            alt={feature.title}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.3s ease',
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ textAlign: 'center', px: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {feature.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 }}
                >
                    <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            sx={styles.primaryBtn}
                            href="/?createAccount=true"
                            endIcon={<ArrowForwardIcon />}
                        >
                            Create Your Own AI Persona
                        </Button>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
}

// ============ HOW IT WORKS SECTION ============
const steps = [
    {
        number: "1",
        title: "Design Your Persona",
        description: "Upload text, video, or audio content to shape your persona's knowledge and style.",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/how-it-works/HIW1.png"
    },
    {
        number: "2",
        title: "Train & Refine",
        description: "Continuously update your persona in real-time to keep it relevant and aligned with your expertise.",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/how-it-works/HIW2.png"
    },
    {
        number: "3",
        title: "Monetize Your Insights",
        description: "Launch paid Q&A sessions, courses, or premium chats directly through Soopra's platform.",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/how-it-works/HIW3.png"
    },
    {
        number: "4",
        title: "Scale Your Reach",
        description: "Engage your audience globally, 24/7, without sacrificing the personal touch.",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/how-it-works/HIW4.png"
    }
];

function HowItWorks() {
    return (
        <Box component="section" sx={{ ...styles.section, py: { xs: 12, lg: 16 } }}>
            <Box sx={styles.gradientBg} />
            <Container sx={{ ...styles.container, position: 'relative' }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            textAlign: 'center',
                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        How It Works
                    </Typography>
                </motion.div>

                <Grid container spacing={4} sx={{ mt: { xs: 4, lg: 8 } }}>
                    {steps.map((step, index) => (
                        <Grid item xs={12} sm={6} lg={3} key={step.title}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.2 }}
                            >
                                <Box sx={{ position: 'relative' }}>
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            mb: 4,
                                            aspectRatio: '1/1',
                                            overflow: 'hidden',
                                            borderRadius: 6,
                                            background: 'linear-gradient(to bottom right, rgba(239, 246, 255, 1), white)',
                                            p: 3,
                                        }}
                                    >
                                        <Box
                                            component="img"
                                            src={step.image}
                                            alt={step.title}
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                p: 3,
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 16,
                                                top: 16,
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                backgroundColor: styles.accentColor,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: '0.875rem',
                                            }}
                                        >
                                            {step.number}
                                        </Box>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                                            {step.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            {step.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 }}
                >
                    <Box sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
                        <Button variant="contained" sx={styles.primaryBtn} href="/?createAccount=true">
                            Create Your AI Persona
                        </Button>
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
}

// ============ IMPACT SECTION ============
const metrics = [
    {
        value: 2.5,
        suffix: "x",
        label: "Audience Engagement",
        description: "Users interact with AI personas longer, boosting brand loyalty.",
        Icon: UsersIcon,
    },
    {
        value: 10,
        suffix: "x",
        label: "Increased Revenue Streams",
        description: "Offer premium sessions, private Q&As, and curated learning paths—no middleman.",
        Icon: TrendingUpIcon,
    },
    {
        value: 90,
        suffix: "%",
        label: "Time Saved",
        description: "Automate repetitive questions so you can focus on creativity, growth, and innovation.",
        Icon: ClockIcon,
    }
];

interface CounterProps {
    value: number;
    suffix?: string;
    duration?: number;
}

function Counter({ value, suffix, duration = 1000 }: CounterProps) {
    const [count, setCount] = useState(0);
    const elementRef = useRef<HTMLSpanElement>(null);
    const isInView = useInView(elementRef, { once: true });

    useEffect(() => {
        if (isInView) {
            let start = 0;
            const end = value;
            const incrementTime = duration / (end * 10);
            const counter = setInterval(() => {
                start += 0.1;
                setCount(Math.min(start, end));
                if (start >= end) clearInterval(counter);
            }, incrementTime);

            return () => clearInterval(counter);
        }
    }, [value, duration, isInView]);

    return (
        <span ref={elementRef} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {count.toFixed(1).replace(/\.0$/, '')}
            {suffix}
        </span>
    );
}

function ImpactSection() {
    return (
        <Box component="section" sx={{ ...styles.section, py: { xs: 12, lg: 16 } }}>
            <Box sx={styles.gradientBg} />
            <Container sx={{ ...styles.container, position: 'relative' }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <Typography
                        variant="h3"
                        sx={{
                            textAlign: 'center',
                            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                            fontWeight: 700,
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Real Results, Real Impact
                    </Typography>
                </motion.div>

                <Grid container spacing={4} sx={{ mt: { xs: 4, lg: 8 } }}>
                    {metrics.map((metric, index) => (
                        <Grid item xs={12} lg={4} key={metric.label}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.2 }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            mb: 3,
                                            width: 64,
                                            height: 64,
                                            borderRadius: '50%',
                                            backgroundColor: `${styles.accentColor}1A`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <metric.Icon sx={{ fontSize: 32, color: styles.accentColor }} />
                                    </Box>
                                    <Typography
                                        sx={{
                                            mb: 2,
                                            fontSize: '3rem',
                                            fontWeight: 700,
                                            letterSpacing: '-0.02em',
                                        }}
                                    >
                                        <Counter value={metric.value} suffix={metric.suffix} />
                                    </Typography>
                                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                        {metric.label}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ textAlign: 'center', color: 'text.secondary' }}
                                    >
                                        {metric.description}
                                    </Typography>
                                </Box>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
}

// ============ TESTIMONIALS SECTION ============
const testimonials = [
    {
        name: "Ronjon Nag",
        role: "Founder R42, Inventor,\nAdjunct Prof. Stanford Medicine",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/expert-images/ronjon.png",
        quote: "Using Soopra AI, my students are able to talk to me even when I'm not there."
    },
    {
        name: "Bill Harvey",
        role: "Executive Chairman,\nBill Harvey Consulting, Inc.",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/expert-images/expert-1734713813954-bill+harvey.png",
        quote: "Having Experimented with many AIs, I'm delighted that Soopra is something I can trust to risk my reputation on."
    },
    {
        name: "Frank Sortino",
        role: "Professor Emeritus in Finance and Director of\nthe Pension Research Institute",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/expert-images/Frank.png",
        quote: "Sometimes students think their questions are embarrassing, so asking my AI is an easy way for them to get the answers they want."
    },
    {
        name: "Chuck Young",
        role: "CEO,\nAmeritest Global Research",
        image: "https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/expert-images/expert-1734713924162-Charles+Edward+Young.png",
        quote: "Surprisingly easy to train your Soopra, using conversational onboarding."
    }
];

function TestimonialsSection() {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const autoPlayRef = useRef<NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {
        if (isAutoPlaying) {
            startAutoPlay();
        }
        return () => {
            if (autoPlayRef.current) clearInterval(autoPlayRef.current);
        };
    }, [isAutoPlaying]);

    const startAutoPlay = () => {
        if (autoPlayRef.current) clearInterval(autoPlayRef.current);
        autoPlayRef.current = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % testimonials.length);
        }, 5000);
    };

    const handlePrevious = () => {
        setIsAutoPlaying(false);
        setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    const handleNext = () => {
        setIsAutoPlaying(false);
        setActiveIndex((prev) => (prev + 1) % testimonials.length);
    };

    return (
        <Box component="section" sx={{ ...styles.section, py: 12 }}>
            <Box sx={styles.gradientBg} />
            <Container sx={{ ...styles.container, position: 'relative' }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                >
                    <Typography
                        variant="h4"
                        sx={{
                            textAlign: 'center',
                            maxWidth: '900px',
                            mx: 'auto',
                            fontWeight: 700,
                            fontSize: { xs: '1.5rem', sm: '1.875rem', lg: '2.25rem' },
                            letterSpacing: '-0.02em',
                        }}
                    >
                        Access insights from a global community of renowned authors, artists, entrepreneurs, and educators who've built thriving AI personas on Soopra.
                    </Typography>
                </motion.div>

                <Box sx={{ mt: 10 }}>
                    <Box sx={{ position: 'relative', height: 400, overflow: 'hidden' }}>
                        <AnimatePresence mode="wait">
                            {testimonials.map((testimonial, idx) => (
                                idx === activeIndex && (
                                    <motion.div
                                        key={testimonial.name}
                                        initial={{ opacity: 0, x: 100 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -100 }}
                                        transition={{ duration: 0.5 }}
                                        style={{ position: 'absolute', inset: 0 }}
                                    >
                                        <Grid container spacing={6} alignItems="center" sx={{ height: '100%' }}>
                                            <Grid item xs={12} md={6}>
                                                <Box
                                                    sx={{
                                                        position: 'relative',
                                                        width: '100%',
                                                        height: 400,
                                                        borderRadius: 4,
                                                        overflow: 'hidden',
                                                        background: 'linear-gradient(to bottom right, rgba(239, 246, 255, 1), white)',
                                                        boxShadow: 3,
                                                    }}
                                                >
                                                    <Box
                                                        component="img"
                                                        src={testimonial.image}
                                                        alt={testimonial.name}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'contain',
                                                            p: 2,
                                                        }}
                                                    />
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                    <QuoteIcon sx={{ fontSize: 48, color: `${styles.accentColor}33` }} />
                                                    <Typography
                                                        sx={{
                                                            mt: 2,
                                                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                                            fontWeight: 500,
                                                            fontStyle: 'italic',
                                                            color: 'text.primary',
                                                        }}
                                                    >
                                                        "{testimonial.quote}"
                                                    </Typography>
                                                    <Box sx={{ mt: 4 }}>
                                                        <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                            {testimonial.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ mt: 0.5, whiteSpace: 'pre-line', color: 'text.secondary' }}
                                                        >
                                                            {testimonial.role}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </motion.div>
                                )
                            ))}
                        </AnimatePresence>
                    </Box>

                    {/* Navigation */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 8 }}>
                        <Button
                            variant="outlined"
                            onClick={handlePrevious}
                            sx={{ minWidth: 40, width: 40, height: 40, borderRadius: '50%', p: 0 }}
                        >
                            <ChevronLeftIcon />
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {testimonials.map((_, idx) => (
                                <Box
                                    key={idx}
                                    component="button"
                                    onClick={() => {
                                        setIsAutoPlaying(false);
                                        setActiveIndex(idx);
                                    }}
                                    sx={{
                                        width: idx === activeIndex ? 32 : 8,
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: idx === activeIndex ? styles.accentColor : '#d1d5db',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                    }}
                                />
                            ))}
                        </Box>
                        <Button
                            variant="outlined"
                            onClick={handleNext}
                            sx={{ minWidth: 40, width: 40, height: 40, borderRadius: '50%', p: 0 }}
                        >
                            <ChevronRightIcon />
                        </Button>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}

// ============ MAIN PAGE COMPONENT ============
export default function ForExpertsPage() {
    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            <ForExpertsHero />
            <CreatorsSection />
            <HowItWorks />
            <ImpactSection />
            <TestimonialsSection />
        </Box>
    );
}
